import type {
  SideMenuNavItemProps,
  SocialNetwork,
  TrackLinks,
} from "@partners/ui";
import type { UserProfileOnboardingState } from "@partners/ui/api";
import type { MouseEvent, ReactNode } from "react";

import {
  IconButton,
  SideMenu,
  SideMenuContent,
  SideMenuFooter,
  SideMenuProfileBanner,
  SideMenuSeparator,
  SocialLinks,
  useFeatureFlags,
} from "@partners/ui";
import { Typo } from "@pepperstone/pepper-ui-kit";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { LAUNCH_PERSONAL_TRADING_URL } from "@/components/Header";
import { useGlobalAnalytics } from "@/hooks/analytics";
import { useTriageHistoryPageAnalytics } from "@/hooks/analytics/useTriageHistoryPageAnalytics";
import { useTriagePageAnalytics } from "@/hooks/analytics/useTriagePageAnalytics";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useLogout } from "@/hooks/useLogout";
import { isServedToChina } from "@/utils/domainResolver";
import { logger } from "@/utils/logger";
import { openInSameTab } from "@/utils/openInSameTab";

import {
  LAUNCH_MAM_URL,
  LAUNCH_PROFILE_URL,
  LAUNCH_TRANSFER_URL,
  LAUNCH_WITHDRAW_URL,
  LeaveFeedbackNavItem,
  LogoutNavItem,
  MAMNavItem,
  ProfileMenuLaunchPersonalTradingNavItem,
  TransferNavItem,
  TriageAddProgramNavItem,
  TriageHistoryNavItem,
  WithdrawNavItem,
} from "./ProfileMenuNavItems";

export type NavItem = Omit<SideMenuNavItemProps, "onClick">;

export type NavGroup = NavItem[];

export type ProfileMenuNavGroups = NavGroup[];

export type ProfileMenuProps = {
  userName: string;
  isIdentityOnlyUser: boolean;
  onboardingState: UserProfileOnboardingState;
  isMAMMaster: boolean;
  isTriageEnabled: boolean;
  hasTriagesCompleted: boolean;
};

type ViewProfileLinkProps = { className: string; children: ReactNode };

export const ProfileMenu = ({
  userName,
  isIdentityOnlyUser,
  onboardingState,
  isMAMMaster,
  isTriageEnabled,
  hasTriagesCompleted,
}: ProfileMenuProps) => {
  const {
    trackSocialLinkProfileMenuEvent,
    trackViewProfileProfileMenuEvent,
    trackWithdrawProfileMenuEvent,
    trackLaunchPersonalTradingHeaderEvent,
    trackTransferProfileMenuEvent,
    trackMAMProfileMenuEvent,
  } = useGlobalAnalytics();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const isMobile = useIsMobile();
  const { logout } = useLogout();
  const { t } = useTranslation();
  const { programSelectionPage } = useFeatureFlags();

  const translatedContent = {
    welcomeText: t("global_profileMenu_welcome_header"),
    viewProfileLabel: t("global_profileMenu_viewProfile_link"),
    followUsLabel: t("global_profileMenu_followUs_label"),
    launchPersonalTradingLabel: t(
      "global_profileMenu_launchPersonalTrading_link",
    ),
    withdrawLabel: t("global_profileMenu_withdraw_link"),
    transferLabel: t("global_profileMenu_transfer_link"),
    mamLabel: "MAM",
    triageHistoryLabel: t(
      "global_profileMenu_partners_application_summary_link",
    ),
    triageProgramLabel: t(
      "global_profileMenu_partners_application_program_link",
    ),
    leaveFeedbackLabel: t("global_profileMenu_leaveFeedback_link"),
    logoutLabel: t("global_profileMenu_logout_link"),
  };

  const getTrackFn =
    (socialNetwork: SocialNetwork) => (event: MouseEvent<HTMLAnchorElement>) =>
      trackSocialLinkProfileMenuEvent(event, socialNetwork);

  const { trackViewAppSummaryProfileMenuEvent } =
    useTriageHistoryPageAnalytics();
  const { trackAddAnotherProgramProfileMenuEvent } = useTriagePageAnalytics();

  const trackSocialLinks: TrackLinks = {
    X: getTrackFn("X"),
    Facebook: getTrackFn("Facebook"),
    Instagram: getTrackFn("Instagram"),
    Linkedin: getTrackFn("Linkedin"),
    Youtube: getTrackFn("Youtube"),
  };

  const ViewProfileLink = ({ className, children }: ViewProfileLinkProps) => (
    <a
      href={LAUNCH_PROFILE_URL}
      className={className}
      onClick={(event) => {
        setIsOpen(false);
        openInSameTab(
          LAUNCH_PROFILE_URL,
          trackViewProfileProfileMenuEvent,
        )(event);
      }}
    >
      {children}
    </a>
  );

  return (
    <>
      <IconButton
        icon="profile"
        size="medium"
        color="white"
        aria-label="Profile menu"
        onClick={() => setIsOpen(true)}
      />

      <SideMenu isOpen={isOpen} onClose={() => setIsOpen(false)}>
        <SideMenuContent>
          <SideMenuProfileBanner
            profileLabel={isIdentityOnlyUser ? undefined : userName}
            welcomeText={translatedContent.welcomeText}
            viewProfileLabel={
              isIdentityOnlyUser
                ? undefined
                : translatedContent.viewProfileLabel
            }
            linkComponent={ViewProfileLink}
          />
          {isMobile && !isIdentityOnlyUser && (
            <ProfileMenuLaunchPersonalTradingNavItem
              label={translatedContent.launchPersonalTradingLabel}
              onClick={(event) => {
                if (isServedToChina()) {
                  openInSameTab(
                    LAUNCH_PERSONAL_TRADING_URL,
                    trackLaunchPersonalTradingHeaderEvent,
                  )(event);
                }
              }}
            />
          )}
          {onboardingState === "IBApplicationComplete" && (
            <>
              <WithdrawNavItem
                label={translatedContent.withdrawLabel}
                onClick={(event) => {
                  setIsOpen(false);
                  openInSameTab(
                    LAUNCH_WITHDRAW_URL,
                    trackWithdrawProfileMenuEvent,
                  )(event);
                }}
              />
              <TransferNavItem
                label={translatedContent.transferLabel}
                onClick={(event) => {
                  setIsOpen(false);
                  openInSameTab(
                    LAUNCH_TRANSFER_URL,
                    trackTransferProfileMenuEvent,
                  )(event);
                }}
              />
              <SideMenuSeparator />
            </>
          )}
          {isMAMMaster && (
            <MAMNavItem
              onClick={(event) => {
                setIsOpen(false);
                openInSameTab(LAUNCH_MAM_URL, trackMAMProfileMenuEvent)(event);
              }}
            />
          )}
          {isTriageEnabled && (
            <>
              {hasTriagesCompleted && (
                <TriageHistoryNavItem
                  label={translatedContent.triageHistoryLabel}
                  onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                    setIsOpen(false);
                    trackViewAppSummaryProfileMenuEvent(event);
                  }}
                />
              )}
              <TriageAddProgramNavItem
                label={translatedContent.triageProgramLabel}
                onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                  setIsOpen(false);
                  trackAddAnotherProgramProfileMenuEvent(event);
                }}
                href={programSelectionPage ? "/program-selection" : "/triage"}
              />
            </>
          )}
          {(isMAMMaster || isTriageEnabled) && <SideMenuSeparator />}
          <LeaveFeedbackNavItem
            label={translatedContent.leaveFeedbackLabel}
            onClick={() => {
              setIsOpen(false);
            }}
          />
          <LogoutNavItem
            label={translatedContent.logoutLabel}
            onClick={() => {
              setIsOpen(false);
              logout({ returnTo: "/" }).catch((error) => logger.error(error));
            }}
          />
        </SideMenuContent>
        <SideMenuFooter>
          <div className="flex flex-col gap-4">
            <div className="text-text-secondary/80">
              <Typo elem="p" type="label" size="md">
                {translatedContent.followUsLabel}
              </Typo>
            </div>
            <SocialLinks trackLinks={trackSocialLinks} />
          </div>
        </SideMenuFooter>
      </SideMenu>
    </>
  );
};
